import { useApp } from 'contexts/app';
import { useDialog } from 'contexts/dialog';
import { useJoyride } from 'contexts/joyride';
import { useSnackbar } from 'contexts/snackbar';
import { omit } from 'lodash';
import { createContext, useCallback, useEffect, useState } from 'react';
import { Redirect, useHistory, useParams } from 'react-router-dom';
import submissionService from 'services/submission';
import {
    DEFAULT_FEEDBACK_LANGUAGE_LEVEL,
    DEFAULT_FEEDBACK_NUM_SUGGESTIONS,
    DEFAULT_FEEDBACK_STRUCTURE,
    DEFAULT_FEEDBACK_SUGGESTION_LENGTH,
    DEFAULT_FEEDBACK_TONE,
} from 'utils/constants';
import { getAIPreview, getTemplatedFeedback, getToneDescription, stripHTML } from 'utils/helper';

const PageContext = createContext();

const PageProvider = (props) => {
    const { currentAssignment, submissions: allSubmissions, setSubmissions } = useApp();
    const { setDialog, closeDialog } = useDialog();
    const history = useHistory();
    const { setJoyridePaused } = useJoyride();
    const { id: currentAssignmentId, submissionId: currentSubmissionId } = useParams();
    const { message } = useSnackbar();

    const { joyrideRun, setNextCallback, setPrevCallback, performNext } = useJoyride();

    const [form, setForm] = useState({
        criteria: {},
        feedback: null,
    });
    const [tab, setTab] = useState('grades');
    const [selectedCriterionId, setSelectedCriterionId] = useState(null);
    const [selectedFeedbackId, setSelectedFeedbackId] = useState(null);
    const [isGradesGenerating, setIsGradesGenerating] = useState(false);
    const [isFeedbackGenerating, setIsFeedbackGenerating] = useState(false);
    const [showFailedDialog, setShowFailedDialog] = useState(false);
    const [contentBankResourcesUsageDialog, setContentBankResourcesUsageDialog] = useState(false);
    const [isDocumentFullScreen, setIsDocumentFullScreen] = useState(false);

    const submissions = allSubmissions
        ?.filter(({ ai, grading, is_failed, locked }) => ((ai || is_failed) && !grading) || locked)
        ?.sort((a, b) => {
            const aName = a.data.name.toLowerCase();
            const bName = b.data.name.toLowerCase();
            if (aName < bName) return -1;
            if (aName > bName) return 1;
            return 0;
        });
    const currentSubmission = submissions?.find(({ id }) => id === currentSubmissionId);

    const selectedCriterionTitle = currentAssignment?.rubric?.criteria?.find(
        ({ id }) => id === selectedCriterionId
    )?.title;

    const getHighlights = () => {
        let highlights;
        if (tab === 'grades') {
            highlights = currentSubmission?.ai?.grading?.[selectedCriterionId]?.locations || [];
        }
        if (tab === 'feedback') {
            if (currentSubmission.ai.feedback.feedback_type === 'rubric') {
                highlights =
                    form.feedback?.improvement_suggestions?.find(({ id }) => id === selectedFeedbackId)?.negative
                        ?.locations || [];
            } else {
                highlights =
                    form.feedback?.improvement_suggestions?.find(({ id }) => id === selectedFeedbackId)?.locations ||
                    [];
            }
        }
        return highlights.map((item) => ({ id: selectedFeedbackId, value: item }));
    };

    const validateForm = useCallback(() => {
        if (!currentAssignment) return false;
        const isGradingValid = currentAssignment.rubric.criteria.every(({ id }) => Boolean(form.criteria[id]));
        const isFeedbackValid = Boolean(form.feedback);

        if (currentAssignment?.ai_feedback_enabled && currentAssignment?.ai_grading_enabled) {
            return isGradingValid && isFeedbackValid;
        } else if (currentAssignment?.ai_feedback_enabled) {
            return isFeedbackValid;
        } else if (currentAssignment?.ai_grading_enabled) {
            return isGradingValid;
        }
    }, [currentAssignment, form.criteria, form.feedback]);

    let isCurrentFormValid = validateForm();

    const goToNextSubmission = () => {
        let nextSubmissionIndex = submissions.findIndex(({ id }) => id === currentSubmission.id) + 1;
        if (nextSubmissionIndex === submissions.length) nextSubmissionIndex = 0;
        const nextSubmission = submissions[nextSubmissionIndex];
        const nextSubmissionId = nextSubmission.id;
        history.push(`/assignments/${currentAssignmentId}/grading/${nextSubmissionId}`);
        setForm({
            criteria: nextSubmission?.grading?.criteria || {},
            feedback: nextSubmission?.ai?.feedback || null,
        });
    };

    const handleClickConfirm = () => {
        if (joyrideRun) return performNext();
        document.getElementById('actions-container').scroll({ top: 0, behavior: 'smooth' });
        const updatedForm =
            currentSubmission.ai?.feedback?.feedback_type === 'templated'
                ? {
                      ...form,
                      feedback: {
                          ...form.feedback,
                          commentary: getTemplatedFeedback(currentAssignment, form),
                      },
                  }
                : form;
        setSubmissions((current) => {
            return current.map((submission) => {
                return submission.id === currentSubmissionId
                    ? { ...currentSubmission, locked: true, grading: updatedForm }
                    : submission;
            });
        });
        submissionService.updateSubmission(currentSubmissionId, {
            ...omit(currentSubmission, ['_id', 'locked', 'is_failed']),
            grading: updatedForm,
            modified_on: new Date().getTime(),
        });
    };

    const handleClickNext = () => {
        if (!currentSubmission.locked) {
            setDialog({
                open: true,
                message: 'Incomplete Steps',
                description: 'If you continue to the next submission, your unconfirmed grades/feedback will be lost.',
                note: '',
                variant: 'destructive',
                actionButtonText: 'Continue',
                cancelButtonText: 'Cancel',
                onAction: () => {
                    goToNextSubmission();
                    closeDialog();
                },
                onCancel: () => closeDialog(),
                onClose: () => closeDialog(),
            });
        } else {
            goToNextSubmission();
        }
    };

    const handleSelectSubmission = (submissionId) => {
        if (!currentSubmission.locked) {
            setDialog({
                open: true,
                message: 'Incomplete Steps',
                description: 'If you continue to the next submission, your unconfirmed grades/feedback will be lost.',
                note: '',
                variant: 'destructive',
                actionButtonText: 'Continue',
                cancelButtonText: 'Cancel',
                onAction: () => {
                    history.push(`/assignments/${currentAssignmentId}/grading/${submissionId}`);
                    closeDialog();
                },
                onCancel: () => closeDialog(),
                onClose: () => closeDialog(),
            });
        } else {
            history.push(`/assignments/${currentAssignmentId}/grading/${submissionId}`);
        }
    };

    const handleClickClose = () => {
        setSubmissions((current) => {
            return current.map((submission) => omit(submission, ['locked']));
        });
        history.push(`/assignments/${currentAssignmentId}`);
    };

    const handleClickEdit = () => {
        setSubmissions((current) => {
            return current.map((submission) => {
                if (submission.id !== currentSubmission.id) return submission;
                setTimeout(() => {
                    setForm({ ...currentSubmission.grading });
                }, 0);
                return {
                    ...omit(currentSubmission, ['grading']),
                    locked: false,
                };
            });
        });
    };

    const handleSelectDropdown = async ({ key }) => {
        if (key === 'regenerate-grades') {
            setDialog({
                open: true,
                message: 'Regenerate Suggested Grades?',
                description: 'This will delete any existing grades permanently.',
                note: '',
                variant: 'primary',
                actionButtonText: 'Regenerate',
                cancelButtonText: 'Cancel',
                onAction: async () => {
                    closeDialog();
                    setIsGradesGenerating(true);
                    try {
                        const response = await getAIPreview({
                            owner_account_id: currentAssignment.owner_account_id,
                            target: 'grading',
                            assignment_id: currentAssignment.id,
                            description: currentAssignment.description,
                            rubric: currentAssignment.rubric,
                            baselineAssignment: currentAssignment.baseline?.assignment,
                            baselineCriteria: currentAssignment.baseline?.criteria,
                            content_bank_resources: currentAssignment.content_bank_resources,
                            structure: currentAssignment.ai_feedback_settings.structure || DEFAULT_FEEDBACK_STRUCTURE,
                            is_rag_ready: currentAssignment.is_rag_ready || false,
                            numSuggestions:
                                currentAssignment.ai_feedback_settings.num_suggestions ||
                                DEFAULT_FEEDBACK_NUM_SUGGESTIONS,
                            suggestionLength:
                                currentAssignment.ai_feedback_settings.suggestion_length ||
                                DEFAULT_FEEDBACK_SUGGESTION_LENGTH,
                            languageLevel:
                                currentAssignment.ai_feedback_settings.language_level ||
                                DEFAULT_FEEDBACK_LANGUAGE_LEVEL,
                            tone: currentAssignment.ai_feedback_settings.tone || DEFAULT_FEEDBACK_TONE,
                            toneDescription: getToneDescription(currentAssignment.ai_feedback_settings.tone),
                            customInstructions: currentAssignment.ai_feedback_settings.custom_instructions || '',
                            feedbackCustomContext: currentAssignment.ai_feedback_context,
                            gradingCustomContext: currentAssignment.ai_grading_context,
                            submission: currentSubmission.data,
                        });
                        const result = await response.json();
                        const updatedSubmission = omit(
                            {
                                ...currentSubmission,
                                modified_on: new Date().getTime(),
                                ai: {
                                    ...currentSubmission.ai,
                                    grading: result.data.grading,
                                },
                                grading: null,
                            },
                            ['locked']
                        );
                        setSubmissions((current) => {
                            return current.map((submission) => {
                                if (submission.id !== updatedSubmission.id) return submission;
                                submissionService.updateSubmission(
                                    updatedSubmission.id,
                                    omit(updatedSubmission, ['_id'])
                                );
                                return updatedSubmission;
                            });
                        });
                    } catch (e) {
                        console.error(e);
                        message.error('Something went wrong.');
                    } finally {
                        setIsGradesGenerating(false);
                    }
                },
                onCancel: () => closeDialog(),
                onClose: () => closeDialog(),
            });
        }
        if (key === 'regenerate-feedback') {
            setDialog({
                open: true,
                message: 'Regenerate Suggested Feedback?',
                description: 'This will delete any existing feedback permanently.',
                note: '',
                variant: 'primary',
                actionButtonText: 'Regenerate',
                cancelButtonText: 'Cancel',
                onAction: async () => {
                    closeDialog();
                    setIsFeedbackGenerating(true);
                    try {
                        const response = await getAIPreview({
                            owner_account_id: currentAssignment.owner_account_id,
                            target: 'feedback',
                            assignment_id: currentAssignment.id,
                            description: currentAssignment.description,
                            rubric: currentAssignment.rubric,
                            baselineAssignment: currentAssignment.baseline?.assignment,
                            baselineCriteria: currentAssignment.baseline?.criteria,
                            content_bank_resources: currentAssignment.content_bank_resources,
                            structure: currentAssignment.ai_feedback_settings.structure || DEFAULT_FEEDBACK_STRUCTURE,
                            numSuggestions:
                                currentAssignment.ai_feedback_settings.num_suggestions ||
                                DEFAULT_FEEDBACK_NUM_SUGGESTIONS,
                            suggestionLength:
                                currentAssignment.ai_feedback_settings.suggestion_length ||
                                DEFAULT_FEEDBACK_SUGGESTION_LENGTH,
                            languageLevel:
                                currentAssignment.ai_feedback_settings.language_level ||
                                DEFAULT_FEEDBACK_LANGUAGE_LEVEL,
                            tone: currentAssignment.ai_feedback_settings.tone || DEFAULT_FEEDBACK_TONE,
                            toneDescription: getToneDescription(currentAssignment.ai_feedback_settings.tone),
                            customInstructions: currentAssignment.ai_feedback_settings.custom_instructions || '',
                            feedbackCustomContext: currentAssignment.ai_feedback_context,
                            gradingCustomContext: currentAssignment.ai_grading_context,
                            submission: currentSubmission.data,
                        });
                        const result = await response.json();
                        let updatedSubmission = {
                            ...currentSubmission,
                            modified_on: new Date().getTime(),
                            ai: {
                                ...currentSubmission.ai,
                                feedback: result.data.feedback,
                            },
                        };
                        if (updatedSubmission.grading) {
                            updatedSubmission.grading.feedback = result.data.feedback;
                        }
                        setSubmissions((current) => {
                            return current.map((submission) => {
                                if (submission.id !== updatedSubmission.id) return submission;
                                submissionService.updateSubmission(
                                    updatedSubmission.id,
                                    omit(updatedSubmission, ['_id'])
                                );
                                return updatedSubmission;
                            });
                        });
                    } catch (e) {
                        console.error(e);
                        message.error('Something went wrong.');
                    } finally {
                        setIsFeedbackGenerating(false);
                    }
                },
                onCancel: () => closeDialog(),
                onClose: () => closeDialog(),
            });
        }
        if (key === 'copy-all') {
            let feedback = '';
            if (typeof currentSubmission.ai?.feedback === 'string') {
                feedback += stripHTML(form.feedback);
            } else if (currentSubmission.ai?.feedback?.feedback_type === 'rubric') {
                form.feedback.improvement_suggestions.forEach(({ criterion_id, positive, negative }, index) => {
                    if (index) feedback += '\n\n';
                    const criterion = currentAssignment.rubric.criteria.find(({ id }) => id === criterion_id);
                    feedback += `${criterion.title}\n\n`;
                    feedback += 'What you did well\n\n';
                    feedback += !positive.active ? 'This feedback was deleted.' : stripHTML(positive.text);
                    feedback += '\n\nHow you can improve\n\n';
                    feedback += !negative.active ? 'This feedback was deleted.' : stripHTML(negative.text);
                });
            } else if (currentSubmission.ai?.feedback?.feedback_type === 'system') {
                feedback += stripHTML(form.feedback.commentary);
                form.feedback.improvement_suggestions
                    .filter(({ active }) => active)
                    .forEach(({ text }) => {
                        feedback += '\n\n';
                        feedback += stripHTML(text);
                    });
            }
            await navigator.clipboard.writeText(feedback);
            message.success('Copied to clipboard.');
        }
        if (key === 'delete') {
            setDialog({
                open: true,
                message: 'Delete All Feedback?',
                description: 'Are you sure you want to delete all feedback? This can’t be undone.',
                note: '',
                variant: 'destructive',
                actionButtonText: 'Delete',
                cancelButtonText: 'Cancel',
                onAction: () => {
                    if (currentSubmission.ai?.feedback?.feedback_type === 'rubric') {
                        setForm((current) => {
                            return {
                                ...current,
                                feedback: {
                                    commentary: '',
                                    improvement_suggestions: current.feedback.improvement_suggestions.map((item) => ({
                                        ...item,
                                        positive: {
                                            ...item.positive,
                                            active: false,
                                        },
                                        negative: {
                                            ...item.negative,
                                            active: false,
                                        },
                                    })),
                                },
                            };
                        });
                    }
                    if (currentSubmission.ai?.feedback?.feedback_type === 'system') {
                        setForm((current) => {
                            return {
                                ...current,
                                feedback: {
                                    commentary: '',
                                    improvement_suggestions: current.feedback.improvement_suggestions.map((item) => ({
                                        ...item,
                                        active: false,
                                    })),
                                },
                            };
                        });
                    }
                    closeDialog();
                },
                onCancel: () => closeDialog(),
                onClose: () => closeDialog(),
            });
        }
    };

    useEffect(() => {
        if (submissions && !submissions.length) {
            history.push(`/assignments/${currentAssignmentId}`);
        }
    }, [currentAssignmentId, history, submissions]);

    useEffect(() => {
        if (submissions?.length && !currentSubmissionId) {
            const nextSubmissionId = submissions[0].id;
            history.replace(`/assignments/${currentAssignmentId}/grading/${nextSubmissionId}`);
        }
    }, [currentAssignmentId, currentSubmissionId, history, submissions]);

    useEffect(() => {
        if (!currentAssignment) return;
        if (currentSubmission) {
            if (currentSubmission.grading) {
                setForm({
                    criteria: currentSubmission.grading.criteria,
                    feedback: currentSubmission.grading.feedback,
                });
                if (['rubric', 'system'].includes(currentSubmission?.ai?.feedback?.feedback_type)) {
                    const activeSuggestions = currentSubmission.grading.feedback.improvement_suggestions.filter(
                        ({ active }) => active
                    );
                    if (activeSuggestions.length) setSelectedFeedbackId(activeSuggestions[0].id);
                }
            } else if (currentSubmission.ai) {
                setForm({
                    criteria: {},
                    feedback: currentSubmission.ai.feedback,
                });
                if (['rubric', 'system'].includes(currentSubmission.ai.feedback?.feedback_type)) {
                    if (currentSubmission.ai.feedback.improvement_suggestions?.length) {
                        setSelectedFeedbackId(currentSubmission.ai.feedback.improvement_suggestions[0].id);
                    }
                }
            } else {
                setForm({ criteria: {}, feedback: null });
            }
            setSelectedCriterionId(currentAssignment.rubric.criteria[0].id);
            if (!currentSubmission?.ai?.grading && currentSubmission?.ai?.feedback) {
                setTab('feedback');
            } else {
                setTab('grades');
                document.getElementById('actions-container').scrollTop = 0;
            }
        } else {
            setSelectedCriterionId(null);
            setSelectedFeedbackId(null);
        }
    }, [currentAssignment, currentSubmission]);

    useEffect(() => {
        setJoyridePaused(false);
        return () => {
            setJoyridePaused(true);
        };
    }, [setJoyridePaused]);

    useEffect(() => {
        setNextCallback({
            8: () => setTab('feedback'),
            9: () => {
                setForm((current) => ({
                    ...current,
                    criteria: {
                        c1: 'c1-o1',
                        c2: 'c2-o2',
                        c3: 'c3-o2',
                        c4: 'c4-o3',
                    },
                }));
            },
        });
        setPrevCallback({
            8: () => setTab('grades'),
            10: () => {
                setForm((current) => ({ ...current, criteria: {} }));
            },
        });
    }, [setNextCallback, setPrevCallback]);

    if (submissions?.length && currentSubmissionId && !currentSubmission) {
        return <Redirect to={`/assignments/${currentAssignmentId}`} />;
    }

    return (
        <PageContext.Provider
            value={{
                currentAssignment,
                submissions,
                currentSubmissionId,
                currentSubmission,
                selectedCriterionTitle,
                highlights: getHighlights(),
                form,
                setForm,
                isCurrentFormValid,
                tab,
                setTab,
                selectedCriterionId,
                setSelectedCriterionId,
                selectedFeedbackId,
                setSelectedFeedbackId,
                isGradesGenerating,
                isFeedbackGenerating,
                showFailedDialog,
                setShowFailedDialog,
                contentBankResourcesUsageDialog,
                setContentBankResourcesUsageDialog,
                isDocumentFullScreen,
                setIsDocumentFullScreen,
                handleClickConfirm,
                handleClickNext,
                handleSelectSubmission,
                handleClickClose,
                handleClickEdit,
                handleSelectDropdown,
            }}
            {...props}
        />
    );
};

export { PageContext };
export default PageProvider;
